import { ApplicationConfig, PluginKey } from "resource-planner-model";

export interface PluginFieldView {
  label: string;
  value: string;
}

export interface PluginCatalogEntry {
  key: PluginKey;
  label: string;
  description: string;
  hasEnabledFlag: boolean;
}

export const PLUGIN_CATALOG: PluginCatalogEntry[] = [
  {
    key: "jira",
    label: "Jira",
    description:
      "Project sync and customer communication fields sourced from Jira.",
    hasEnabledFlag: true,
  },
  {
    key: "harvest",
    label: "Harvest",
    description:
      "Time and budget data plus the Harvest-to-Teams reporting workflow.",
    hasEnabledFlag: true,
  },
  {
    key: "microsoftShifts",
    label: "Microsoft Shifts",
    description:
      "Time-off and marked-out sync from Microsoft Teams Shifts via Graph.",
    hasEnabledFlag: true,
  },
  {
    key: "microsoftTenant",
    label: "Microsoft tenant",
    description:
      "Shared Microsoft Graph tenant settings used by Microsoft integrations.",
    hasEnabledFlag: false,
  },
];

export function pluginEntry(key: PluginKey): PluginCatalogEntry {
  const entry = PLUGIN_CATALOG.find((candidate) => candidate.key === key);
  if (!entry) {
    throw new Error(`Unknown plugin key: ${key}`);
  }
  return entry;
}

export function isPluginEnabled(
  config: ApplicationConfig,
  key: PluginKey
): boolean | undefined {
  switch (key) {
    case "jira":
      return config.plugins.jira.enabled;
    case "harvest":
      return config.plugins.harvest.enabled;
    case "microsoftShifts":
      return config.plugins.microsoftShifts.enabled;
    default:
      return undefined;
  }
}

function list(values: string[]): string {
  return values.length ? values.join(", ") : "—";
}

export function pluginSummary(
  config: ApplicationConfig,
  key: PluginKey
): PluginFieldView[] {
  const { jira, harvest, microsoftShifts, microsoftTenant } = config.plugins;
  switch (key) {
    case "jira":
      return [
        { label: "Host", value: jira.host || "—" },
        { label: "Sync schedule", value: jira.syncSchedule || "—" },
        { label: "EM groups", value: list(jira.groupIdsEM) },
        { label: "Engineer groups", value: list(jira.groupIdsEngineer) },
      ];
    case "harvest":
      return [
        {
          label: "Harvest-to-Teams schedule",
          value: harvest.harvestToTeamsSchedule || "—",
        },
        {
          label: "Cache TTL (s)",
          value: String(harvest.cacheExpirationInSeconds),
        },
        { label: "Ops task IDs", value: list(harvest.opsTaskIds) },
      ];
    case "microsoftShifts":
      return [
        { label: "Team IDs", value: list(microsoftShifts.teamIds) },
        {
          label: "Marked-out sync",
          value: microsoftShifts.markedOutSyncEnabled ? "On" : "Off",
        },
      ];
    case "microsoftTenant":
      return [
        { label: "Tenant ID", value: microsoftTenant.tenantId || "—" },
        { label: "Graph endpoint", value: microsoftTenant.graphEndpoint },
        { label: "Graph API version", value: microsoftTenant.graphApiVersion },
        { label: "Scopes", value: list(microsoftTenant.scopes) },
      ];
    default:
      return [];
  }
}

export function withPluginEnabled(
  config: ApplicationConfig,
  key: PluginKey,
  enabled: boolean
): ApplicationConfig {
  if (key === "microsoftTenant") {
    return config;
  }
  return {
    ...config,
    plugins: {
      ...config.plugins,
      [key]: {
        ...config.plugins[key],
        enabled,
      },
    },
  };
}
